<script setup lang="ts">
/**
 * 消息通知, as Volcengine's bell is: a panel of the newest 站内信 — each a
 * title and its time, the unread marked — under a head with 全部已读, this
 * console's 待处理 line (NavAttention) when there is something, and at
 * the foot 查看更多 for the 消息中心 and 消息接收配置. The count is the
 * unread plus what is 待处理. Square and hung from the bar's rule as the
 * navbar's other panels are (.nav-panel). The same file in both consoles.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { InboxItem } from '@/api/client'
import { inbox, loadInbox, markRead } from '@/utils/inbox'
import { navbarHeight, navPanel, quietTooltip } from '@/utils/layout'
import { formatDateTime } from '@/utils/format'
import NavAttention from './NavAttention.vue'
import NavIcon from './NavIcon.vue'

const { t } = useI18n()
const router = useRouter()

// One panel out at a time (navPanel), as the account's and 工单's.
const open = computed(() => navPanel.value === 'notifications')
function onVisible(v: boolean) {
  if (v) navPanel.value = 'notifications'
  else if (open.value) navPanel.value = null
}
// Hung from the bar's rule: the 32px button sits in the bar's middle.
const offset = computed(() => (navbarHeight.value - 32) / 2 + 1)
// The tooltip keeps out of the panel's way (quietTooltip).
const { visible: tipVisible, change: onTip } = quietTooltip(open)

const attention = ref(0)
const count = computed(() => inbox.unread + attention.value)

function load() {
  void loadInbox().catch(() => {
    // A bell that cannot ring is not worth an error toast on every page.
  })
}
let timer: number | undefined
onMounted(() => {
  load()
  timer = window.setInterval(load, 120_000)
})
onUnmounted(() => window.clearInterval(timer))
watch(open, (v) => v && load())

function go(name: string) {
  navPanel.value = null
  router.push({ name })
}
// A message read where it is opened: its page, or the 消息中心 showing it.
function pick(n: InboxItem) {
  navPanel.value = null
  if (!n.read_at) void markRead([n.id]).catch(() => {})
  if (n.link) router.push(n.link)
  else router.push({ name: 'messages', query: { id: String(n.id) } })
}
// 消息接收配置 in a page of its own, as the arrow says.
function openSettings() {
  navPanel.value = null
  window.open(router.resolve({ name: 'message-settings' }).href, '_blank', 'noopener')
}
</script>

<template>
  <a-trigger
    :popup-visible="open"
    trigger="click"
    position="br"
    :popup-offset="offset"
    @popup-visible-change="onVisible"
  >
    <span class="bell">
      <a-badge :count="count" :max-count="99">
        <a-tooltip :content="t('app.notifications')" :popup-visible="tipVisible" @popup-visible-change="onTip">
          <a-button class="nav-btn" type="text" shape="square" :class="{ open }">
            <template #icon><NavIcon name="bell" /></template>
          </a-button>
        </a-tooltip>
      </a-badge>
    </span>
    <template #content>
      <div class="nav-panel notice">
        <div class="notice-head">
          <span class="notice-title">{{ t('app.notifications') }}</span>
          <span v-if="inbox.unread" class="notice-unread">{{ t('inbox.unreadCount', { n: inbox.unread }) }}</span>
          <a-link v-if="inbox.unread" class="notice-all" @click="markRead([]).catch(() => {})">{{ t('inbox.readAll') }}</a-link>
        </div>
        <NavAttention :open="open" @count="(n) => (attention = n)" @go="go" />
        <div class="notice-list">
          <button v-for="n in inbox.recent" :key="n.id" type="button" class="notice-item" :class="{ unread: !n.read_at }" @click="pick(n)">
            <span class="notice-item-title">{{ n.title }}</span>
            <span class="notice-item-time">{{ formatDateTime(n.created_at) }}</span>
          </button>
          <div v-if="inbox.loaded && !inbox.recent.length" class="notice-empty">
            <NavIcon name="bell" class="notice-empty-icon" />
            {{ t('inbox.empty') }}
          </div>
        </div>
        <div class="notice-foot">
          <button type="button" class="notice-foot-link" @click="go('messages')">
            {{ t('inbox.more') }}<icon-right />
          </button>
          <button type="button" class="notice-foot-link" @click="openSettings">
            {{ t('inbox.settings') }}<icon-launch />
          </button>
        </div>
      </div>
    </template>
  </a-trigger>
</template>

<style scoped>
.bell {
  display: inline-flex;
}
.notice {
  width: 360px;
  max-width: 100vw;
}
/* A phone: the window's width, hung from the bar as the menu drawer is. */
@media (max-width: 767px) {
  .notice {
    width: 100vw;
    border-right: none;
    border-left: none;
  }
}
.notice-head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid var(--nav-rule);
}
.notice-title {
  color: var(--color-text-1);
  font-size: 14px;
  font-weight: 500;
}
.notice-unread {
  color: var(--color-text-3);
  font-size: 12px;
}
.notice-all {
  margin-left: auto;
  padding: 0;
  font-size: 13px;
}
/* Volcengine's rows: the title one line, the time under it in grey; the
   unread in full black with a dot before them. */
.notice-item {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 12px 16px 12px 28px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.1s;
}
.notice-item:hover {
  background-color: var(--color-fill-2);
}
.notice-item-title {
  overflow: hidden;
  color: var(--color-text-2);
  font-size: 14px;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notice-item.unread .notice-item-title {
  color: var(--color-text-1);
}
/* On the row, not the title: the title clips what stands outside it. */
.notice-item.unread::before {
  content: '';
  position: absolute;
  top: 20px;
  left: 14px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--danger-6));
}
.notice-item-time {
  color: var(--color-text-3);
  font-size: 13px;
  line-height: 20px;
  font-variant-numeric: tabular-nums;
}
.notice-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--color-text-3);
  font-size: 13px;
}
.notice-empty-icon {
  width: 28px;
  height: 28px;
  color: var(--color-text-4);
}
.notice-foot {
  display: flex;
  justify-content: space-between;
  height: 48px;
  padding: 0 16px;
  border-top: 1px solid var(--nav-rule);
}
.notice-foot-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-2);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.notice-foot-link:hover {
  color: rgb(var(--primary-6));
}
</style>
